import { shortRuns } from "./doc/short-runs.js";
import { clipRangeOf, isImageCellValue, bgmStemsOf, } from "./doc/schema.js";
import { GRID_ORIGIN } from "./board-space.js";
import { assetExtensionFor } from "./asset-names.js";
import { segmentMarkedText } from "./text-marks.js";
import { BUILTIN_CELL_TEXT_TYPES } from "./text-types.js";
import { formatClipTime, isYouTubeVideoUrl, youtubeStartMs } from "./video-links.js";
/* ------------------------------------------------------------------ */
/* Manifest                                                            */
/* ------------------------------------------------------------------ */
export const STORYBOARD_FORMAT = "kola-storyboard";
export const STORYBOARD_VERSION = 1;
/** `assets/<assetId>.<ext>` — `bin` when the type is unknown to the doc. */
export function defaultAssetFileName(assetId, hint) {
    const ext = hint.contentType === undefined ? "bin" : assetExtensionFor(hint.contentType);
    return `assets/${assetId}.${ext}`;
}
/* ------------------------------------------------------------------ */
/* Build                                                               */
/* ------------------------------------------------------------------ */
export function buildStoryboard(board, sheetId, options) {
    const sheet = board.listSheets().find((s) => s.id === sheetId);
    const sheetName = sheet?.name ?? "";
    const rowIds = board.sortedRowIds(sheetId);
    const colIds = board.sortedColIds(sheetId);
    const sourceColId = board.sourceColumnId(sheetId);
    const fileFor = options.assetFileName ?? defaultAssetFileName;
    const kindLabels = new Map();
    for (const id of BUILTIN_CELL_TEXT_TYPES)
        kindLabels.set(id, id);
    for (const kind of options.kinds ?? [])
        kindLabels.set(kind.id, kind.label.toLowerCase());
    const labelOf = (id) => kindLabels.get(id) ?? id;
    const assets = new Map();
    const addAsset = (asset) => {
        if (!assets.has(asset.assetId))
            assets.set(asset.assetId, asset);
    };
    const usedKinds = new Map();
    const columns = colIds.map((id) => {
        const info = board.getColumn(sheetId, id);
        const column = { id, name: info?.name ?? "", type: info?.type ?? "text" };
        if (id === sourceColId)
            column.script = true;
        return column;
    });
    const columnName = new Map(columns.map((c) => [c.id, c.name]));
    /* Attachments, grouped by where they land. */
    const byRow = new Map();
    const preamble = [];
    const epilogue = [];
    const bands = options.rowBands ?? bandsFromHeights(board, sheetId, rowIds);
    const rowIndex = new Map(rowIds.map((id, i) => [id, i]));
    const elements = board.listElements();
    const elementById = new Map(elements.map((el) => [el.id, el]));
    const slotCache = new Map();
    const slotOf = (el, depth = 0) => {
        const cached = slotCache.get(el.id);
        if (cached !== undefined)
            return cached;
        let slot = null;
        const anchor = el.anchor;
        if (anchor.kind === "cell") {
            slot = rowIndex.has(anchor.rowId) ? { kind: "row", rowId: anchor.rowId } : null;
        }
        else if (anchor.kind === "element") {
            const parent = elementById.get(anchor.elementId);
            slot = parent !== undefined && depth < 8 ? slotOf(parent, depth + 1) : slotByPosition(el, bands);
        }
        else {
            slot = slotByPosition(el, bands);
        }
        slotCache.set(el.id, slot);
        return slot;
    };
    // Paint order is (z, id); a reader wants reading order — top to bottom,
    // then left to right — so floating attachments sort by position.
    const positioned = [...elements].sort((a, b) => a.y - b.y || a.x - b.x || a.z - b.z);
    for (const el of positioned) {
        const slot = slotOf(el);
        if (slot === null)
            continue;
        const attachment = attachmentOf(el, slot.kind === "row", columnName, fileFor, addAsset);
        if (attachment === null)
            continue;
        if (slot.kind === "preamble")
            preamble.push(attachment);
        else if (slot.kind === "epilogue")
            epilogue.push(attachment);
        else {
            const list = byRow.get(slot.rowId);
            if (list === undefined)
                byRow.set(slot.rowId, [attachment]);
            else
                list.push(attachment);
        }
    }
    const rows = rowIds.map((rowId, i) => {
        const cells = [];
        for (const colId of colIds) {
            const cell = cellOf(board, sheetId, rowId, colId, columnName.get(colId) ?? "", labelOf, usedKinds, fileFor, addAsset);
            if (cell !== null)
                cells.push(cell);
        }
        return { index: i + 1, id: rowId, cells, attachments: byRow.get(rowId) ?? [] };
    });
    const manifest = {
        format: STORYBOARD_FORMAT,
        version: STORYBOARD_VERSION,
        boardId: options.boardId,
        boardName: options.boardName ?? board.getBoardName(),
        sheet: { id: sheetId, name: sheetName },
        generatedAt: (options.now ?? (() => new Date()))().toISOString(),
        columns,
        kinds: [...usedKinds].map(([id, label]) => ({ id, label })),
        preamble,
        rows,
        epilogue,
        assets: [...assets.values()],
    };
    const design = designOf(board);
    if (design !== undefined)
        manifest.design = design;
    const shorts = shortsOf(board, sheetId, rowIds, options.shorts ?? []);
    if (shorts.length > 0)
        manifest.shorts = shorts;
    return manifest;
}
/** F-142: the sheet's shorts, from its `Short` runs, joined with the week's slots. */
function shortsOf(board, sheetId, rowIds, slots) {
    const position = new Map(rowIds.map((id, i) => [id, i + 1]));
    return shortRuns(board, sheetId).map((run) => {
        const slot = slots.find((entry) => entry.slot === run.slot);
        const short = {
            slot: run.slot,
            rows: run.rowIds.map((id) => position.get(id)).filter((n) => n !== undefined),
        };
        if (slot !== undefined) {
            if (slot.title)
                short.title = slot.title;
            short.releaseDate = slot.releaseDate;
            short.state = slot.state;
        }
        return short;
    });
}
/** Row bands stacked from the grid origin by STORED heights (no layout). */
function bandsFromHeights(board, sheetId, rowIds) {
    let y = GRID_ORIGIN.y;
    return rowIds.map((rowId) => {
        const height = board.getRow(sheetId, rowId)?.height ?? 0;
        const band = { rowId, top: y, bottom: y + height };
        y += height;
        return band;
    });
}
function slotByPosition(el, bands) {
    if (bands.length === 0)
        return { kind: "epilogue" };
    const centre = el.y + el.h / 2;
    const first = bands[0];
    const last = bands[bands.length - 1];
    if (centre < first.top)
        return { kind: "preamble" };
    if (centre >= last.bottom)
        return { kind: "epilogue" };
    // Bands are contiguous and ordered; a centre inside the grid hits one, and
    // one that lands in a gap (a caller's bands need not touch) takes the row
    // it is nearest to above — still an ordered reading.
    let hit = first;
    for (const band of bands) {
        if (centre >= band.top)
            hit = band;
        else
            break;
    }
    return { kind: "row", rowId: hit.rowId };
}
function attachmentOf(el, inRow, columnName, fileFor, addAsset) {
    const base = {
        elementId: el.id,
        placement: el.anchor.kind === "cell" && inRow ? "cell" : "beside",
    };
    if (el.anchor.kind === "cell" && inRow) {
        base.columnId = el.anchor.colId;
        base.column = columnName.get(el.anchor.colId) ?? "";
    }
    const payload = el.payload;
    switch (payload.kind) {
        case "image": {
            const file = fileFor(payload.assetId, {});
            const asset = { assetId: payload.assetId, file, kind: "image" };
            if (payload.name !== undefined)
                asset.name = payload.name;
            addAsset(asset);
            const image = {
                ...base,
                kind: "image",
                assetId: payload.assetId,
                file,
                w: payload.w,
                h: payload.h,
            };
            if (payload.name !== undefined)
                image.name = payload.name;
            return image;
        }
        case "file": {
            const file = fileFor(payload.assetId, { contentType: payload.contentType });
            addAsset({
                assetId: payload.assetId,
                file,
                kind: fileKind(payload.media),
                name: payload.name,
                contentType: payload.contentType,
            });
            const attachment = {
                ...base,
                kind: fileKind(payload.media),
                assetId: payload.assetId,
                file,
                name: payload.name,
                contentType: payload.contentType,
                size: payload.size,
            };
            if (payload.w !== undefined)
                attachment.w = payload.w;
            if (payload.h !== undefined)
                attachment.h = payload.h;
            return attachment;
        }
        case "linkCard":
            return linkOf(base, payload, fileFor, addAsset);
        case "text": {
            const text = payload.text.trim();
            return text.length === 0 ? null : { ...base, kind: "note", text };
        }
        case "drawing":
            return null; // ink is not a storyboard instruction
        default:
            return null;
    }
}
function fileKind(media) {
    return media;
}
function linkOf(base, payload, fileFor, addAsset) {
    const link = { ...base, kind: "link", url: payload.url };
    if (payload.title !== undefined)
        link.title = payload.title;
    if (payload.description !== undefined)
        link.description = payload.description;
    if (payload.provider !== undefined)
        link.provider = payload.provider;
    if (payload.media === "video")
        link.video = true;
    if (isYouTubeVideoUrl(payload.url))
        link.youtube = true;
    const clip = clipOf(payload);
    if (clip !== undefined)
        link.clip = clip;
    if (payload.thumbnailAssetId !== undefined) {
        const file = fileFor(payload.thumbnailAssetId, {});
        addAsset({ assetId: payload.thumbnailAssetId, file, kind: "thumbnail" });
        link.thumbnail = { assetId: payload.thumbnailAssetId, file };
    }
    return link;
}
/** The card's range, else the URL's `t=` as a start — see `youtubeStartMs`. */
export function clipOf(payload) {
    const range = clipRangeOf(payload);
    const clip = {};
    if (range !== undefined) {
        if (range.startMs !== undefined) {
            clip.startMs = range.startMs;
            clip.start = formatClipTime(range.startMs);
        }
        if (range.endMs !== undefined) {
            clip.endMs = range.endMs;
            clip.end = formatClipTime(range.endMs);
        }
        return clip;
    }
    const fromUrl = youtubeStartMs(payload.url);
    if (fromUrl === undefined)
        return undefined;
    clip.startMs = fromUrl;
    clip.start = formatClipTime(fromUrl);
    clip.fromUrl = true;
    return clip;
}
function cellOf(board, sheetId, rowId, colId, column, labelOf, usedKinds, fileFor, addAsset) {
    const snapshot = board.getCell(sheetId, rowId, colId);
    if (snapshot === undefined)
        return null;
    const cell = { columnId: colId, column, text: "" };
    if (isImageCellValue(snapshot.v)) {
        const file = fileFor(snapshot.v.assetId, {});
        addAsset({ assetId: snapshot.v.assetId, file, kind: "image" });
        cell.image = { assetId: snapshot.v.assetId, file, w: snapshot.v.w, h: snapshot.v.h };
        return cell;
    }
    const bgm = board.getCellBgm(sheetId, rowId, colId);
    if (bgm !== undefined) {
        cell.text = bgm.title;
        cell.bgm = bgmOf(bgm, fileFor, addAsset);
        return cell;
    }
    const text = board.getCellText(sheetId, rowId, colId);
    if (text.trim().length === 0)
        return null;
    cell.text = text;
    const runs = runsOf(text, snapshot.marks, snapshot.ty, labelOf);
    if (runs !== undefined) {
        cell.runs = runs;
        for (const run of runs)
            if (run.kind !== undefined)
                usedKinds.set(kindIdOf(snapshot.ty, run.kind, labelOf), run.kind);
    }
    return cell;
}
/** The stored id behind a label — for the manifest's `kinds` table. */
function kindIdOf(switches, label, labelOf) {
    for (const sw of switches ?? [])
        if (sw.ty !== undefined && labelOf(sw.ty) === label)
            return sw.ty;
    return label;
}
function bgmOf(bgm, fileFor, addAsset) {
    const stems = bgmStemsOf(bgm).map((stem) => {
        const out = { title: stem.title };
        if (stem.source === "url" && stem.url !== undefined)
            out.url = stem.url;
        if (stem.source === "asset" && stem.assetId !== undefined) {
            const file = fileFor(stem.assetId, {});
            addAsset({ assetId: stem.assetId, file, kind: "audio", name: stem.title });
            out.assetId = stem.assetId;
            out.file = file;
        }
        if (stem.durationMs !== undefined)
            out.durationMs = stem.durationMs;
        return out;
    });
    return { ...stems[0], stems };
}
/** The cell's runs, or undefined when it is one plain narration run. */
export function runsOf(text, marks, switches, labelOf) {
    const segments = segmentMarkedText(text, marks, switches);
    const runs = segments.map((seg) => {
        const run = { text: seg.text };
        if (seg.type !== undefined)
            run.kind = labelOf(seg.type);
        if (seg.bold)
            run.bold = true;
        if (seg.strike)
            run.strike = true;
        if (seg.color !== undefined)
            run.color = seg.color;
        if (seg.hl !== undefined)
            run.highlight = seg.hl;
        return run;
    });
    // Merge neighbours that only differ by nothing (segmentMarkedText cuts at
    // every edge of every kind, so two spans of the same look can touch).
    const merged = [];
    for (const run of runs) {
        const last = merged[merged.length - 1];
        if (last !== undefined && sameLook(last, run))
            last.text += run.text;
        else
            merged.push({ ...run });
    }
    if (merged.length === 1 && Object.keys(merged[0]).length === 1)
        return undefined;
    return merged;
}
function sameLook(a, b) {
    return (a.kind === b.kind &&
        a.bold === b.bold &&
        a.strike === b.strike &&
        a.color === b.color &&
        a.highlight === b.highlight);
}
function designOf(board) {
    if (!board.hasArtDirection())
        return undefined;
    const art = board.getArtDirection();
    const design = {};
    for (const field of ["logline", "mood", "perspective", "lighting", "texture", "motion", "type", "avoid"]) {
        const value = art[field].trim();
        if (value.length > 0)
            design[field] = value;
    }
    if (art.palette.length > 0)
        design.palette = art.palette.map((s) => ({ hex: s.hex, name: s.name }));
    return Object.keys(design).length === 0 ? undefined : design;
}
/* ------------------------------------------------------------------ */
/* Markdown                                                            */
/* ------------------------------------------------------------------ */
/**
 * The manifest as a brief. Conventions an agent can follow without a
 * schema, stated once at the top of the file:
 *  - `## Row N` sections in order; the beat is the row;
 *  - the script column comes first in each row, other columns after;
 *  - a run of a kind is bracketed `[sound] …` / `[visual] …` on its own
 *    line; narration is plain;
 *  - attachments are a list: `Image:`, `Video:`, `Audio:`, `File:`,
 *    `Link:`, `Note:` — with the asset file path relative to this file;
 *  - a link's clip range reads `clip 1:23 → 1:41`.
 */
export function storyboardToMarkdown(manifest) {
    const out = [];
    out.push(`# Storyboard — ${manifest.boardName || manifest.boardId}`);
    out.push("");
    out.push(`- Board: ${manifest.boardId}`);
    out.push(`- Sheet: ${manifest.sheet.name || manifest.sheet.id}`);
    out.push(`- Generated: ${manifest.generatedAt}`);
    out.push(`- Rows: ${manifest.rows.length}`);
    out.push(`- Machine-readable copy: storyboard.json (same folder)`);
    out.push("");
    out.push("## How to read this");
    out.push("");
    const shorts = manifest.shorts ?? [];
    const firstRowOfShort = new Map(shorts.map((short) => [short.rows[0], short]));
    if (shorts.length > 0) {
        out.push("- This board is a WEEK OF SHORTS: the rows are grouped under `Short N` headings, one short each, in the order they release. Each short is its own video.");
    }
    out.push(`- Each \`Row N\` is one beat of the ${shorts.length > 0 ? "short" : "video"}, in order from first to last.`);
    const script = manifest.columns.find((c) => c.script);
    if (script !== undefined) {
        out.push(`- **${script.name || "Original Script"}** is the narration the host reads; the other columns are notes on that beat.`);
    }
    out.push("- A line starting with `[sound]` is a sound-effect cue; `[visual]` is a visual or animation direction; any other bracketed word is a custom line kind. Unbracketed text is narration.");
    out.push("- `Image:`, `Video:`, `Audio:` and `File:` name files in the `assets/` folder next to this file.");
    out.push("- `Link:` is a reference; a YouTube link with `clip a → b` means use exactly that stretch of the video as B-roll.");
    out.push("- `Note:` is a sticky note placed beside that row.");
    out.push("");
    if (manifest.design !== undefined) {
        out.push("## Design theme");
        out.push("");
        const d = manifest.design;
        for (const [label, value] of [
            ["Logline", d.logline],
            ["Mood", d.mood],
            ["Perspective", d.perspective],
            ["Lighting", d.lighting],
            ["Texture", d.texture],
            ["Motion", d.motion],
            ["Type", d.type],
            ["Avoid", d.avoid],
        ]) {
            if (value !== undefined)
                out.push(`- **${label}:** ${oneLine(value)}`);
        }
        if (d.palette !== undefined) {
            out.push(`- **Palette:** ${d.palette.map((s) => `${s.hex} (${s.name})`).join(", ")}`);
        }
        out.push("");
    }
    if (manifest.preamble.length > 0) {
        out.push("## Before the first row");
        out.push("");
        for (const a of manifest.preamble)
            out.push(attachmentLine(a));
        out.push("");
    }
    const columnOrder = [...manifest.columns].sort((a, b) => (b.script ? 1 : 0) - (a.script ? 1 : 0));
    const rowHeading = shorts.length > 0 ? "###" : "##";
    for (const row of manifest.rows) {
        const short = firstRowOfShort.get(row.index);
        if (short !== undefined) {
            const meta = [
                short.releaseDate !== undefined ? `releases ${short.releaseDate}` : null,
                short.state !== undefined ? short.state : null,
            ].filter((part) => part !== null);
            out.push(`## Short ${short.slot}${short.title ? ` — ${short.title}` : ""}${meta.length > 0 ? ` (${meta.join(" · ")})` : ""}`);
            out.push("");
        }
        out.push(`${rowHeading} Row ${row.index}`);
        out.push("");
        let wrote = false;
        for (const column of columnOrder) {
            const cell = row.cells.find((c) => c.columnId === column.id);
            if (cell === undefined)
                continue;
            wrote = true;
            out.push(...cellLines(cell, column));
        }
        for (const a of row.attachments) {
            wrote = true;
            out.push(attachmentLine(a));
        }
        if (!wrote)
            out.push("_(empty row)_");
        out.push("");
    }
    if (manifest.epilogue.length > 0) {
        out.push("## After the last row");
        out.push("");
        for (const a of manifest.epilogue)
            out.push(attachmentLine(a));
        out.push("");
    }
    return `${out.join("\n").trimEnd()}\n`;
}
function cellLines(cell, column) {
    const name = column.name || column.id;
    if (cell.image !== undefined) {
        return [`**${name}:** Image: ${cell.image.file} (${cell.image.w}×${cell.image.h})`];
    }
    if (cell.bgm !== undefined) {
        const listed = cell.bgm.stems.map((stem) => {
            const where = stem.file ?? stem.url ?? "";
            return `${stem.title}${where ? ` — ${where}` : ""}`;
        });
        const label = listed.length === 1 ? "Music" : `Music (${listed.length} stems to choose from)`;
        return [`**${name}:** ${label}: ${listed.join("; ")}`];
    }
    if (cell.runs === undefined)
        return [`**${name}:** ${indentContinuation(cell.text)}`];
    // Kinds go on their own lines so a cue never hides mid-sentence; marks
    // inline. The label is on the FIRST line when the cell opens with
    // narration, otherwise the first kind line follows the label directly.
    const lines = [];
    let current = "";
    let currentKind;
    const flush = () => {
        if (current.trim().length === 0) {
            current = "";
            return;
        }
        const body = indentContinuation(current.trim());
        lines.push(currentKind === undefined ? body : `[${currentKind}] ${body}`);
        current = "";
    };
    for (const run of cell.runs) {
        if (run.kind !== currentKind) {
            flush();
            currentKind = run.kind;
        }
        current += inlineRun(run);
    }
    flush();
    if (lines.length === 0)
        return [`**${name}:** ${indentContinuation(cell.text)}`];
    const [first, ...rest] = lines;
    return [`**${name}:** ${first}`, ...rest.map((l) => `  ${l}`)];
}
function inlineRun(run) {
    // Marks wrap the run's non-blank core so "**bold **" never breaks the mark.
    const lead = /^\s*/.exec(run.text)?.[0] ?? "";
    const trail = /\s*$/.exec(run.text)?.[0] ?? "";
    const core = run.text.slice(lead.length, run.text.length - trail.length);
    if (core.length === 0)
        return run.text;
    let text = core;
    if (run.bold)
        text = `**${text}**`;
    if (run.strike)
        text = `~~${text}~~`;
    if (run.highlight !== undefined)
        text = `==${text}==`;
    return `${lead}${text}${trail}`;
}
/** Multi-line cell text keeps its breaks, indented under its label. */
function indentContinuation(text) {
    return text.split(/\r?\n/).join("\n  ");
}
function oneLine(text) {
    return text.replace(/\s*\n\s*/g, " ").trim();
}
function attachmentLine(a) {
    const where = a.placement === "cell" && a.column ? ` (in ${a.column})` : "";
    switch (a.kind) {
        case "image":
            return `- Image${where}: ${a.file} (${a.w}×${a.h})${a.name ? ` — ${a.name}` : ""}`;
        case "video":
        case "audio":
        case "file": {
            const label = a.kind === "video" ? "Video" : a.kind === "audio" ? "Audio" : "File";
            const size = a.size > 0 ? ` (${formatBytes(a.size)})` : "";
            return `- ${label}${where}: ${a.file} — ${a.name}${size}`;
        }
        case "link": {
            const parts = [];
            parts.push(a.title !== undefined ? `${a.title} — ${a.url}` : a.url);
            if (a.clip !== undefined)
                parts.push(clipText(a.clip));
            if (a.description !== undefined)
                parts.push(oneLine(a.description));
            return `- Link${where}: ${parts.join(" — ")}`;
        }
        case "note":
            return `- Note${where}: ${oneLine(a.text)}`;
        default:
            return "";
    }
}
/** "clip 1:23 → 1:41", "clip from 1:23", "clip until 1:41". */
export function clipText(clip) {
    if (clip.start !== undefined && clip.end !== undefined)
        return `clip ${clip.start} → ${clip.end}`;
    if (clip.start !== undefined)
        return `clip from ${clip.start}`;
    if (clip.end !== undefined)
        return `clip until ${clip.end}`;
    return "";
}
function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=storyboard.js.map